import {
  type MeasurementKind,
  type PreferenceValues,
  type ResolvedFormats,
  UNIT_CODES,
} from './types';

/**
 * Builds the signature: a short string identifying one exact preference set.
 *
 * This is the memo key for the entire system. The provider's context value, the formatter bundle
 * and the Intl cache all key on it, so a field left out does not merely make the string shorter
 * — it makes a real preference change invisible. Switch capacity from litres to gallons, get the
 * same key back, keep the formatter built for litres.
 *
 * The CDR's `en-US|America/Detroit|MILES,PSI,KWH|MDY,12H,USD` is illustrative and lists three of
 * the nine units. Every field is included here instead, and the structures below exist to make
 * leaving one out a compile error rather than a bug nobody sees for a year.
 */

/**
 * Ordering comes from insertion order, which JavaScript guarantees for string keys, so the `Record`
 * gives exhaustiveness and iteration order at once. Adding a field to `PreferenceValues` without
 * adding it here does not type-check.
 */
const SIGNATURE_PARTS: Record<
  keyof PreferenceValues,
  (values: PreferenceValues) => string
> = {
  locale: ({ locale }) => locale,
  timeZone: ({ timeZone }) => timeZone,
  unitSystem: ({ unitSystem }) => unitSystem,
  units: ({ units }) =>
    (Object.keys(UNIT_CODES) as MeasurementKind[])
      .map((kind) => units[kind])
      .join(','),
  formats: ({ formats }) =>
    FORMAT_ORDER.map((family) => formats[family]).join(','),
};

/**
 * Same trick one level down. `currencyCode` is derived rather than received, so it is not a key of
 * `STYLE_CODES` and has to be listed alongside the families explicitly.
 */
const FORMAT_KEYS: Record<keyof ResolvedFormats, true> = {
  date: true,
  time: true,
  dateTime: true,
  currency: true,
  currencyCode: true,
  phone: true,
  number: true,
  address: true,
  firstDayOfWeek: true,
};

const FORMAT_ORDER = Object.keys(FORMAT_KEYS) as (keyof ResolvedFormats)[];

/**
 * Derives the memo key for a resolved preference set.
 *
 * @param values - Fully resolved preferences, without the signature they produce.
 * @returns A stable string that differs whenever any resolved field differs.
 */
export const signatureOf = (values: PreferenceValues): string =>
  (Object.keys(SIGNATURE_PARTS) as (keyof PreferenceValues)[])
    .map((part) => SIGNATURE_PARTS[part](values))
    .join('|');
